<template>
    <div
        :id="`ds-input_${id}`"
        class="ds-input"
        :class="{
            [`variation-${variation}`]: true,
            'has-error': error,
            'is-disabled': disabled || readonly,
            'is-active': active,
            'is-focused': isFocused,
        }"
    >
        <label>
            <input
                :id="id"
                ref="input"
                :type="type"
                :value="modelValue"
                :maxlength="maxlength"
                :disabled="disabled"
                :name="name"
                :autocomplete="typeof autocomplete !== 'boolean' ? autocomplete : (autocomplete ? 'on' : 'off')"
                :inputmode="inputmode"
                :readonly="readonly"
                :aria-invalid="!!error"
                :aria-readonly="readonly"
                :aria-describedby="error ? errorHelperId : null"
                :placeholder="[2, 3].includes(Number(variation)) ? label : null"
                @keydown="handleKeyDown"
                @focus="event => { isFocused = true; $emit('focus', event); }"
                @blur="event => { isFocused = false; $emit('blur', event); }"
                @input="setValue"
                @copy="onCopy"
                @paste="onPaste"
            />
            <div
                v-if="label"
                class="label"
                :class="{ 'sr-only': [2, 3].includes(Number(variation)) }"
            >
                {{ label }}<span v-if="required" class="required">*</span>
            </div>
            <Transition name="fade">
                <div v-if="iconBefore" class="before">
                    <slot name="before">
                        <component
                            :is="onClickBefore ? 'button' : 'div'"
                            v-if="iconBefore"
                            :type="onClickBefore ? 'button' : null"
                            class="icon-button"
                            @click="onClickBefore"
                        >
                            <DSFontIcon
                                :code="leadingIcon?.code"
                                :icon-style="leadingIcon?.iconStyle"
                                :size="leadingIcon?.size"
                            />
                        </component>
                    </slot>
                </div>
            </Transition>
            <Transition name="fade">
                <div v-if="iconAfter && !resetBtn" class="after">
                    <slot name="after">
                        <component
                            :is="onClickAfter ? 'button' : 'div'"
                            v-if="iconAfter"
                            :type="onClickAfter ? 'button' : null"
                            class="icon-button"
                            @click="onClickAfter"
                        >
                            <DSFontIcon
                                :code="trailingIcon?.code"
                                :size="trailingIcon?.size"
                                :icon-style="trailingIcon?.iconStyle || 'line'"
                            />
                        </component>
                    </slot>
                </div>
                <button v-else-if="resetBtn && modelValue" class="after" @click="resetValue">
                    <DSFontIcon
                        :code="trailingIcon?.code || 'delete'"
                        :size="trailingIcon?.size || 'm'"
                        :icon-style="trailingIcon?.iconStyle || 'line'"
                    />
                </button>
            </Transition>
        </label>
        <div
            v-if="error || helpText"
            :id="errorHelperId"
            class="text-after"
            :class="{
                'help-text': helpText,
                'error': error,
            }"
        >
            {{ error ? error : helpText }}
        </div>
    </div>
</template>
<script>
import { useId } from '#imports';

const maxLengthDefault = 524288;

export default {
    name: 'DSInput',
    props: {
        type: {
            type: String,
            default: 'text',
        },
        required: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: '',
        },
        maxlength: {
            type: [Number, String],
            default: maxLengthDefault,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        resetBtn: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: [String, Number],
            default: '',
        },
        name: {
            type: String,
            default: null,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        autocomplete: {
            type: [String, Boolean],
            default: false,
        },
        helpText: {
            type: String,
            default: null,
        },
        error: {
            type: String,
            default: null,
        },
        iconBefore: {
            type: [Boolean, String, Object],
            default: false,
        },
        iconAfter: {
            type: [Boolean, String, Object],
            default: false,
        },
        disableCopy: {
            type: Boolean,
            default: false,
        },
        disablePaste: {
            type: Boolean,
            default: false,
        },
        onClickBefore: {
            type: Function,
            default: null,
        },
        onClickAfter: {
            type: Function,
            default: null,
        },
        variation: {
            default: 1,
            validator(value) {
                return [1, 2, 3].includes(Number(value));
            },
        },
        inputmode: {
            type: [String, null],
            default: null,
        },
    },
    emits: [
        'focus',
        'blur',
        'submit',
        'update:modelValue',
    ],
    data() {
        return {
            isFocused: false,
            id: null,
        };
    },
    computed: {
        errorHelperId() {
            return `error-helper-${this.id}`;
        },
        active() {
            return !!this.modelValue || this.isFocused;
        },
        leadingIcon() {
            return this.getIcon({ icon: this.iconBefore });
        },
        trailingIcon() {
            return this.getIcon({ icon: this.iconAfter, leading: false });
        },
    },
    created() {
        this.id = this.$attrs.id || `input-${useId()}`;
    },
    mounted() {
        if (this.autofocus) this.$nextTick(() => this.$refs.input.focus());
    },
    methods: {
        handleKeyDown(event) {
            switch (event.key) {
                case 'Enter':
                    event.preventDefault();
                    this.$emit('submit');
                    break;
            }
        },
        setValue(event) {
            if (this.type === 'number' && Number(this.maxlength) && (event.target.value.length > Number(this.maxlength))) {
                const clampedValue = event.target.value.slice(0, this.maxlength);

                this.$refs.input.value = clampedValue;
                this.$emit('update:modelValue', clampedValue);
            } else {
                this.$emit('update:modelValue', event.target.value);
            }
        },
        resetValue() {
            this.$emit('update:modelValue', '');
        },
        onCopy(event) {
            if (this.disableCopy) event.preventDefault();
        },
        onPaste(event) {
            if (this.disablePaste) event.preventDefault();
        },
        focus() {
            this.$refs.input.focus();
        },
        getIcon({ icon, leading = true }) {
            if (!icon) return false;

            const defaultIconSize = leading ? 'xxl' : 'l';

            if (typeof icon === 'boolean') return { code: 'search', size: defaultIconSize, iconStyle: 'line' };
            if (typeof icon === 'string') return { code: icon, size: defaultIconSize };

            return icon;
        },
    },
};
</script>
